import { AcceptSsoInvitationForExistingMemberDto, AcceptSsoInvitationForNewMemberDto, shouldInitializeMemberCrypto, } from '@lucidlink/dto/web-service-api';
import { AES_ENCRYPTION_ALGO_PARAMS, arrayToAesKey, arrayToPrivateKey, base64ToArray, cryptoPairsFromArray, decryptWithAes, decryptWithRsaBlockCipher, encryptCryptoKeyWithAes, encryptWithAes, RSA_ENCRYPTION_ALGO_PARAMS, } from '@lucidlink/ts-crypto/crypto';
import { prepareMemberCommon } from '#sdk/common/member';
import { prepareAcceptIndividualInvitationWithMemberCrypto as prepareAcceptInvitationWithMemberCrypto } from './prepare-accept-invitation-with-member-crypto';
export const prepareAcceptSsoInvitationForNewMemberDto = async (params) => {
    const { acceptableSsoInvitationData, domainKey, userKey } = params;
    const ssoKey = await getSsoKey(domainKey, acceptableSsoInvitationData);
    if (!acceptableSsoInvitationData.ePrivateKeyBySsoSecret) {
        throw new Error('Missing encrypted sso secret');
    }
    const invitationPublicKeyArray = base64ToArray(acceptableSsoInvitationData.publicKey);
    const invitationPrivateKeyArray = (await decryptWithAes(acceptableSsoInvitationData.ePrivateKeyBySsoSecret, ssoKey)).buffer;
    const invitationKeys = await cryptoPairsFromArray(invitationPublicKeyArray, invitationPrivateKeyArray);
    const { member, memberKeys } = await prepareMemberCommon({
        workspaceId: acceptableSsoInvitationData.workspace.id,
        invitationKeys,
    });
    const ePrivateKeyByUserKey = await encryptCryptoKeyWithAes(memberKeys.encryptPair.privateKey, userKey);
    return AcceptSsoInvitationForNewMemberDto.create({
        ...member,
        invitationId: acceptableSsoInvitationData.id,
        ePrivateKeyByUserKey,
    });
};
export const prepareAcceptSsoInvitationForExistingMemberDto = async (params) => {
    const { acceptableSsoInvitationData, userKey, domainKey } = params;
    const ssoKey = await getSsoKey(domainKey, acceptableSsoInvitationData);
    if (!acceptableSsoInvitationData.member) {
        throw new Error('Missing member data for user');
    }
    if (!acceptableSsoInvitationData.ePrivateKeyBySsoSecret) {
        throw new Error('Missing member member private key encrypted by sso secret');
    }
    const invitationPrivateKeyArray = (await decryptWithAes(acceptableSsoInvitationData.ePrivateKeyBySsoSecret, ssoKey)).buffer;
    if (shouldInitializeMemberCrypto({
        ...acceptableSsoInvitationData.member,
        principalId: acceptableSsoInvitationData.member.id,
    })) {
        const preparedInvitationWithCrypto = await prepareAcceptInvitationWithMemberCrypto({
            userKey,
            acceptableInvitationData: acceptableSsoInvitationData,
            member: acceptableSsoInvitationData.member,
            invitationPrivateKeyArray,
        });
        return AcceptSsoInvitationForExistingMemberDto.create(preparedInvitationWithCrypto);
    }
    const invitationPrivateKey = await arrayToPrivateKey(invitationPrivateKeyArray, RSA_ENCRYPTION_ALGO_PARAMS);
    const memberPrivateKeyArray = await decryptWithRsaBlockCipher(acceptableSsoInvitationData.member.ePrivateKeyByInvitationKey, invitationPrivateKey);
    const ePrivateKeyByUserKey = await encryptWithAes(memberPrivateKeyArray, userKey);
    return AcceptSsoInvitationForExistingMemberDto.create({
        invitationId: acceptableSsoInvitationData.id,
        ePrivateKeyByUserKey,
    });
};
const getSsoKey = async (domainKey, acceptableSsoInvitationData) => {
    if (!acceptableSsoInvitationData.eSsoSecretByDomainKey) {
        return domainKey;
    }
    const decryptedSsoSecret = await decryptWithAes(acceptableSsoInvitationData.eSsoSecretByDomainKey, domainKey);
    return arrayToAesKey(new Uint8Array(decryptedSsoSecret.buffer), AES_ENCRYPTION_ALGO_PARAMS);
};
